import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';
import { useApolloClient } from '@apollo/client';
import { Trans } from '@lingui/react';
import { DocumentNode } from 'graphql';
import { Box } from '@components/layout/Grid';
import AnalyticsManager from '@lib/analytics/manager';
import Button from '@components/buttons';
import {
  ProgramCategory,
  ProgramTextElementFieldsFragment,
  useSaveGenericProgramProgressMutation,
} from '@gql/generated';
import DocumentIcon from '@assets/DocumentIcon';
import LanguageContext from '@lib/contexts/languageContext';

const Wrapper = styled.div<{ category: ProgramCategory }>`
  min-height: 60vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 50px 20px;
  background-color: ${({ category }) =>
    category === ProgramCategory['Challenges'] ? '#D9E8E7' : '#e3f4fe'};

  svg {
    height: 64px;
    width: 64px;

    path {
      fill: ${({ category, theme }) => {
        return theme.helpers.getProgramThemeColor(category);
      }};
    }
  }
`;

const Title = styled.p`
  font-size: 20px;
  font-weight: 300;
  line-height: 17px;
  margin: 30px 0 20px 0;
`;

const DescriptionText = styled.p`
  max-width: 650px;
  font-size: 14px;
  text-align: center;
  margin-top: 0;
  margin-bottom: 20px;
`;

const OpenSurvey = styled.button`
  height: 44px;
  color: #1c343e;
  font-size: 13px;
  font-weight: 600;
  line-height: 16px;
  text-decoration: none;
  border: 1px solid;
  border-radius: 3px;
  padding: 11px 24px;
  margin-left: 10px;
  background-color: transparent;
  cursor: pointer;
`;

const JoinProgram = styled.p`
  font-size: 16px;
  color: #1c343e;
  margin-bottom: 20px;

  i {
    margin-right: 6px;

    :before {
      color: ${({ theme }) => theme.colors.secondary['gray-2']};
    }
  }
`;

declare type Props = {
  programTitle: string;
  programId: string;
  programQuery: DocumentNode;
  textElement: ProgramTextElementFieldsFragment;
  programProgress: number;
  hasAccess: boolean;
  programCategory: ProgramCategory;
};

const TextElementWindow = (props: Props) => {
  const client = useApolloClient();
  const lang = useContext(LanguageContext);
  const [saveProgramProgress] = useSaveGenericProgramProgressMutation();

  const {
    programTitle,
    programId,
    textElement,
    hasAccess,
    programQuery,
    programProgress,
    programCategory,
  } = props;

  async function updateVideoProgress() {
    if (!textElement.locked && !textElement.finished) {
      return saveProgramProgress({
        variables: {
          programPartElementId: textElement.id,
          documentPartId: null,
          videoPartId: null,
          liveEventPartId: null,
        },
      });
    }
  }

  function refetchPogram() {
    client
      .query({
        query: programQuery,
        variables: { slug: programId },
        fetchPolicy: 'network-only',
      })
      .then(resp => {
        // send program completed event
        if (programProgress < 100 && resp?.data?.progress === 100) {
          AnalyticsManager().sendProgramCompletionEvent({
            slug: programId,
            category: programCategory,
            title: programTitle,
          });
        }
      });
  }

  useEffect(() => {
    if (!textElement.finished) {
      updateVideoProgress().then(() => {
        refetchPogram();
      });
    }
  }, []);

  return (
    <Box data-testid="VideoPlayerSection">
      <Wrapper category={programCategory} data-testtype="TextElementOverlay">
        <DocumentIcon />
        <Title data-testid="TextElementOverlay--Title">
          {textElement.title}
        </Title>
        {!hasAccess ? (
          <>
            <JoinProgram>
              <Trans id="weeklyProgram.textElement.locked" />
            </JoinProgram>
            <Button
              appearance={
                programCategory === ProgramCategory['Challenges']
                  ? 'green'
                  : 'stone'
              }
              href={`/${lang}/${programCategory}/${programId}/get-started`}
            >
              <Trans id="metadata.getStarted" />
            </Button>
          </>
        ) : (
          <DescriptionText data-testid="TextElementOverlay--Description">
            {textElement.description}
          </DescriptionText>
        )}
      </Wrapper>
    </Box>
  );
};

export default TextElementWindow;
